
import React, { useState, useEffect } from 'react';
import {
    Card, Table, Button, Space, Input, message, Modal, Form,
    Typography, Radio, Tooltip, Tag, Divider, Alert, Upload,
    Row, Col, Menu, Dropdown, Popconfirm, Empty
} from 'antd';
import {
    PlusOutlined, DeleteOutlined, ExclamationCircleOutlined,
    ArrowLeftOutlined, SearchOutlined, CloudDownloadOutlined,
    CloudUploadOutlined, InfoCircleOutlined, DownOutlined,
    CopyOutlined, CheckOutlined, FilterOutlined, EditOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dnsService from '../../services/dnsService';
import apiClient from '../../../shared/services/apiClient';
import { validateCIDR } from '../../../shared/utils/validation';
import Papa from 'papaparse';

import { adminPath } from '../../../shared/config/paths';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { confirm } = Modal;

const DnsCidrEntries = () => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cidr, setCidr] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isBulkModalVisible, setIsBulkModalVisible] = useState(false);
    const [modalTitle, setModalTitle] = useState('添加CIDR条目');
    const [editingEntry, setEditingEntry] = useState(null);
    const [form] = Form.useForm();
    const [bulkForm] = Form.useForm();
    const [searchValue, setSearchValue] = useState('');
    const [selectedEntries, setSelectedEntries] = useState([]);
    const [ipVersionFilter, setIpVersionFilter] = useState('all');
    const navigate = useNavigate();
    const { cidrId } = useParams();

    // 获取CIDR条目列表
    const fetchEntries = async () => {
        setLoading(true);
        try {
            const result = await dnsService.getCidrEntries(cidrId, searchValue);
            if (result.success) {
                setEntries(result.data.entries);
                setCidr(result.data.cidr);
            } else {
                message.error(result.error || '获取CIDR条目失败');
            }
        } catch (error) {
            message.error('获取CIDR条目失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEntries();
    }, [cidrId, searchValue]);

    // 显示添加条目模态框
    const showAddModal = () => {
        setModalTitle('添加CIDR条目');
        setEditingEntry(null);
        form.resetFields();
        form.setFieldsValue({
            cidr_id: parseInt(cidrId),
            ip_version: 'both'
        });
        setIsModalVisible(true);
    };

    // 显示编辑条目模态框
    const showEditModal = (entry) => {
        setModalTitle(`编辑CIDR条目: ${entry.cidr}`);
        setEditingEntry(entry);
        form.resetFields();
        form.setFieldsValue({
            cidr_id: entry.cidr_id,
            cidr: entry.cidr,
            description: entry.description || '',
            ip_version: entry.ip_version || 'both'
        });
        setIsModalVisible(true);
    };

    // 处理模态框提交
    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();

            // 验证CIDR格式
            if (!validateCIDR(values.cidr)) {
                message.error('CIDR格式无效');
                return;
            }

            // 检查IP版本是否与CIDR格式匹配
            const isIPv6 = values.cidr.includes(':');
            if ((values.ip_version === 'ipv4' && isIPv6) || (values.ip_version === 'ipv6' && !isIPv6)) {
                message.error(`IP版本不匹配。你选择了${values.ip_version.toUpperCase()}但提供了${isIPv6 ? 'IPv6' : 'IPv4'}地址。`);
                return;
            }

            setLoading(true);

            if (editingEntry) {
                // 更新条目
                const result = await dnsService.updateCidrEntry({
                    id: editingEntry.id,
                    ...values
                });

                if (result.success) {
                    message.success('CIDR条目更新成功');
                    setIsModalVisible(false);
                    fetchEntries();
                } else {
                    message.error(result.error || '更新CIDR条目失败');
                }
            } else {
                // 创建新条目
                const result = await dnsService.createCidrEntry(values);

                if (result.success) {
                    message.success('CIDR条目添加成功');
                    setIsModalVisible(false);
                    fetchEntries();
                } else {
                    message.error(result.error || '添加CIDR条目失败');
                }
            }
        } catch (error) {
            console.log('Validation failed:', error);
        } finally {
            setLoading(false);
        }
    };

    // 删除条目
    const deleteEntry = (entry) => {
        confirm({
            title: '确认删除',
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除CIDR条目 "${entry.cidr}" 吗？此操作无法撤销。`,
            okText: '删除',
            okType: 'danger',
            cancelText: '取消',
            onOk: async () => {
                try {
                    setLoading(true);
                    const result = await dnsService.deleteCidrEntry(entry.id);

                    if (result.success) {
                        message.success('CIDR条目删除成功');
                        fetchEntries();
                    } else {
                        message.error(result.error || '删除CIDR条目失败');
                    }
                } catch (error) {
                    message.error('删除CIDR条目失败: ' + error.message);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // 批量删除条目
    const bulkDeleteEntries = async () => {
        if (selectedEntries.length === 0) {
            message.warning('请选择要删除的条目');
            return;
        }

        confirm({
            title: '批量删除确认',
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除选中的 ${selectedEntries.length} 个CIDR条目吗？此操作无法撤销。`,
            okText: '删除',
            okType: 'danger',
            cancelText: '取消',
            onOk: async () => {
                setLoading(true);

                try {
                    // 创建所有删除操作的Promise
                    const deletePromises = selectedEntries.map(id =>
                        dnsService.deleteCidrEntry(id)
                    );

                    // 等待所有删除操作完成
                    const results = await Promise.all(deletePromises);

                    // 计算成功和失败的数量
                    const successCount = results.filter(r => r.success).length;
                    const failedCount = results.length - successCount;

                    if (failedCount === 0) {
                        message.success(`成功删除 ${successCount} 个CIDR条目`);
                    } else {
                        message.warning(`成功删除 ${successCount} 个条目，${failedCount} 个条目删除失败`);
                    }

                    // 清除选择
                    setSelectedEntries([]);
                    // 重新加载数据
                    fetchEntries();
                } catch (error) {
                    message.error('批量删除失败: ' + error.message);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // 批量更改IP版本
    const bulkChangeIpVersion = (version) => {
        if (selectedEntries.length === 0) {
            message.warning('请选择要更新的条目');
            return;
        }

        confirm({
            title: '批量更新IP版本',
            icon: <InfoCircleOutlined />,
            content: `确定要将选中的 ${selectedEntries.length} 个CIDR条目的IP版本更改为 ${version === 'ipv4' ? 'IPv4' : version === 'ipv6' ? 'IPv6' : '自动检测'} 吗？`,
            okText: '更新',
            cancelText: '取消',
            onOk: async () => {
                setLoading(true);

                try {
                    // 创建所有更新操作的Promise
                    const updatePromises = selectedEntries.map(id =>
                        dnsService.updateCidrEntry({
                            id,
                            ip_version: version
                        })
                    );

                    // 等待所有更新操作完成
                    const results = await Promise.all(updatePromises);

                    // 计算成功和失败的数量
                    const successCount = results.filter(r => r.success).length;
                    const failedCount = results.length - successCount;

                    if (failedCount === 0) {
                        message.success(`成功更新 ${successCount} 个CIDR条目的IP版本`);
                    } else {
                        message.warning(`成功更新 ${successCount} 个条目，${failedCount} 个条目更新失败`);
                    }

                    // 清除选择
                    setSelectedEntries([]);
                    // 重新加载数据
                    fetchEntries();
                } catch (error) {
                    message.error('批量更新失败: ' + error.message);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // 复制CIDR
    const copyCidr = (cidr) => {
        navigator.clipboard.writeText(cidr).then(() => {
            message.success('CIDR已复制到剪贴板');
        }).catch(err => {
            message.error('复制失败: ' + err.message);
        });
    };

    // 处理搜索
    const handleSearch = (value) => {
        setSearchValue(value);
    };

    // 处理IP版本筛选
    const handleIpVersionFilter = (version) => {
        setIpVersionFilter(version);
    };

    // 下载CIDR配置（走鉴权后的 apiClient，避免 window.open 命中控制台源且无认证）
    const downloadConfig = async (ipVersion = 'all') => {
        try {
            let url = `/api/admin/dns/cidr-download?cidr_id=${cidrId}`;
            if (ipVersion && ipVersion !== 'all') {
                url += `&ip_version=${ipVersion}`;
            }
            const response = await apiClient.get(url, { responseType: 'blob' });
            const disposition = response.headers?.['content-disposition'] || '';
            const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/i);
            const filename = filenameMatch ? filenameMatch[1] : `cidr_${cidrId}.conf`;
            const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'text/plain' }));
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(blobUrl);
        } catch (e) {
            message.error('下载CIDR配置失败: ' + (e?.response?.data?.error || e.message));
        }
    };

    // 显示批量导入模态框
    const showBulkImportModal = () => {
        bulkForm.resetFields();
        setIsBulkModalVisible(true);
    };

    // 处理文件上传
    const handleFileUpload = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            bulkForm.setFieldsValue({
                entries: e.target.result
            });
        };
        reader.readAsText(file);
        return false; // 阻止自动上传
    };

    // 处理批量导入提交
    const handleBulkImportOk = async () => {
        try {
            const values = await bulkForm.validateFields();
            const entries = values.entries.trim();

            if (!entries) {
                message.error('请输入要导入的CIDR条目');
                return;
            }

            // 尝试解析CSV或文本格式
            let cidrEntries = [];

            try {
                // 尝试使用Papa Parse解析CSV
                const parseResult = Papa.parse(entries, { header: false, skipEmptyLines: true });

                if (parseResult.errors.length > 0) {
                    throw new Error('CSV解析错误');
                }

                cidrEntries = parseResult.data.map(row => {
                    if (row.length < 1) {
                        throw new Error('格式错误: 每行至少需要一个CIDR值');
                    }

                    return {
                        cidr: row[0].trim(),
                        description: row.length > 1 ? row[1].trim() : '',
                        ip_version: row.length > 2 ? row[2].trim() : 'both'
                    };
                });
            } catch (csvError) {
                // 如果CSV解析失败，尝试按行分割
                cidrEntries = entries.split('\n')
                    .filter(line => line.trim() !== '')
                    .map(line => {
                        const parts = line.split('|').map(part => part.trim());

                        return {
                            cidr: parts[0],
                            description: parts.length > 1 ? parts[1] : '',
                            ip_version: parts.length > 2 ? parts[2] : 'both'
                        };
                    });
            }

            // 验证CIDR格式
            const invalidEntries = cidrEntries.filter(entry => !validateCIDR(entry.cidr));
            if (invalidEntries.length > 0) {
                message.error(`发现 ${invalidEntries.length} 个无效的CIDR格式`);
                return;
            }

            // 验证IP版本
            const mismatchEntries = cidrEntries.filter(entry => {
                if (entry.ip_version === 'both') return false;

                const isIPv6 = entry.cidr.includes(':');
                return (entry.ip_version === 'ipv4' && isIPv6) || (entry.ip_version === 'ipv6' && !isIPv6);
            });

            if (mismatchEntries.length > 0) {
                message.error(`发现 ${mismatchEntries.length} 个IP版本与CIDR格式不匹配的条目`);
                return;
            }

            setLoading(true);

            // 批量创建条目
            const createPromises = cidrEntries.map(entry =>
                dnsService.createCidrEntry({
                    cidr_id: parseInt(cidrId),
                    ...entry
                })
            );

            const results = await Promise.all(createPromises);

            // 计算成功和失败的数量
            const successCount = results.filter(r => r.success).length;
            const failedCount = results.length - successCount;

            if (failedCount === 0) {
                message.success(`成功导入 ${successCount} 个CIDR条目`);
                setIsBulkModalVisible(false);
                fetchEntries();
            } else {
                message.warning(`成功导入 ${successCount} 个条目，${failedCount} 个条目导入失败`);
            }
        } catch (error) {
            console.log('Validation failed:', error);
            message.error('导入失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // 处理表格选择变化
    const handleSelectionChange = (selectedRowKeys) => {
        setSelectedEntries(selectedRowKeys);
    };

    // 处理全选
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            // 根据当前筛选器选择所有可见条目
            const visibleEntries = entries.filter(entry => {
                if (ipVersionFilter === 'all') return true;
                if (ipVersionFilter === 'ipv4') return !entry.cidr.includes(':');
                if (ipVersionFilter === 'ipv6') return entry.cidr.includes(':');
                return true;
            });
            setSelectedEntries(visibleEntries.map(entry => entry.id));
        } else {
            setSelectedEntries([]);
        }
    };

    // IP版本筛选菜单
    const ipVersionMenu = (
        <Menu onClick={({ key }) => handleIpVersionFilter(key)}>
            <Menu.Item key="all">所有IP版本</Menu.Item>
            <Menu.Item key="ipv4">仅IPv4</Menu.Item>
            <Menu.Item key="ipv6">仅IPv6</Menu.Item>
        </Menu>
    );

    // 批量操作菜单
    const bulkActionMenu = (
        <Menu>
            <Menu.Item key="delete" onClick={bulkDeleteEntries} danger>
                删除所选条目
            </Menu.Item>
            <Menu.SubMenu title="更改IP版本">
                <Menu.Item key="both" onClick={() => bulkChangeIpVersion('both')}>自动检测</Menu.Item>
                <Menu.Item key="ipv4" onClick={() => bulkChangeIpVersion('ipv4')}>IPv4</Menu.Item>
                <Menu.Item key="ipv6" onClick={() => bulkChangeIpVersion('ipv6')}>IPv6</Menu.Item>
            </Menu.SubMenu>
        </Menu>
    );

    // 下载菜单
    const downloadMenu = (
        <Menu>
            <Menu.Item key="all" onClick={() => downloadConfig('all')}>
                所有IP版本
            </Menu.Item>
            <Menu.Item key="ipv4" onClick={() => downloadConfig('ipv4')}>
                仅IPv4
            </Menu.Item>
            <Menu.Item key="ipv6" onClick={() => downloadConfig('ipv6')}>
                仅IPv6
            </Menu.Item>
        </Menu>
    );

    // 表格列配置
    const columns = [
        {
            title: 'CIDR',
            dataIndex: 'cidr',
            key: 'cidr',
            render: (text, record) => (
                <Space>
                    <Text copyable={{ text, tooltips: ['复制', '已复制!'] }}>{text}</Text>
                    {record.cidr.includes(':') ? (
                        <Tag color="blue">IPv6</Tag>
                    ) : (
                        <Tag color="green">IPv4</Tag>
                    )}
                </Space>
            ),
            filteredValue: ipVersionFilter === 'all' ? null : [ipVersionFilter],
            onFilter: (value, record) => {
                if (value === 'ipv4') return !record.cidr.includes(':');
                if (value === 'ipv6') return record.cidr.includes(':');
                return true;
            },
        },
        {
            title: '描述',
            dataIndex: 'description',
            key: 'description',
            render: text => text || '-'
        },
        {
            title: 'IP版本',
            dataIndex: 'ip_version',
            key: 'ip_version',
            render: version => {
                if (!version || version === 'both') return <span>自动检测</span>;
                return <Tag color={version === 'ipv4' ? 'green' : 'blue'}>{version.toUpperCase()}</Tag>;
            }
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            render: text => new Date(text).toLocaleString()
        },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => showEditModal(record)}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除此CIDR条目吗？"
                        onConfirm={() => deleteEntry(record)}
                        okText="是"
                        cancelText="否"
                    >
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                        >
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // 决定要显示哪些条目（基于IP版本筛选）
    const filteredEntries = ipVersionFilter === 'all'
        ? entries
        : entries.filter(entry => {
            const isIPv6 = entry.cidr.includes(':');
            return (ipVersionFilter === 'ipv4' && !isIPv6) || (ipVersionFilter === 'ipv6' && isIPv6);
        });

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={2}>CIDR条目管理: {cidr?.name || ''}</Title>
                <Space>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate(adminPath('/dns/cidr'))}
                    >
                        返回CIDR列表
                    </Button>
                </Space>
            </div>

            {cidr?.description && (
                <Alert
                    message={cidr.description}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Card>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col flex="auto">
                        <Input.Search
                            placeholder="搜索CIDR或描述"
                            allowClear
                            enterButton
                            onSearch={handleSearch}
                        />
                    </Col>
                    <Col>
                        <Dropdown overlay={ipVersionMenu}>
                            <Button>
                                IP版本筛选 <DownOutlined />
                            </Button>
                        </Dropdown>
                    </Col>
                    <Col>
                        <Dropdown overlay={downloadMenu}>
                            <Button icon={<CloudDownloadOutlined />}>
                                下载配置 <DownOutlined />
                            </Button>
                        </Dropdown>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={showAddModal}
                        >
                            添加条目
                        </Button>
                    </Col>
                    <Col>
                        <Button
                            icon={<CloudUploadOutlined />}
                            onClick={showBulkImportModal}
                        >
                            批量导入
                        </Button>
                    </Col>
                </Row>

                {selectedEntries.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <Alert
                            message={
                                <Space>
                                    <span>已选择 {selectedEntries.length} 个条目</span>
                                    <Dropdown overlay={bulkActionMenu}>
                                        <Button size="small">
                                            批量操作 <DownOutlined />
                                        </Button>
                                    </Dropdown>
                                </Space>
                            }
                            type="info"
                            showIcon
                            closable
                            onClose={() => setSelectedEntries([])}
                        />
                    </div>
                )}

                <Table
                    rowSelection={{
                        selectedRowKeys: selectedEntries,
                        onChange: handleSelectionChange
                    }}
                    columns={columns}
                    dataSource={filteredEntries}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: <Empty description="暂无 CIDR 条目" />
                    }}
                    pagination={{
                        showSizeChanger: true,
                        showTotal: total => `共 ${total} 个条目`
                    }}
                />
            </Card>

            {/* 添加/编辑条目模态框 */}
            <Modal
                title={modalTitle}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => setIsModalVisible(false)}
                confirmLoading={loading}
            >
                <Form
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        name="cidr_id"
                        hidden
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="cidr"
                        label="CIDR"
                        rules={[
                            { required: true, message: '请输入CIDR!' },
                            {
                                validator: (_, value) => {
                                    if (!value || validateCIDR(value)) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('请输入有效的CIDR格式，例如: 192.168.1.0/24 或 2001:db8::/64'));
                                }
                            }
                        ]}
                    >
                        <Input placeholder="例如: 192.168.1.0/24 或 2001:db8::/64" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="描述 (可选)"
                    >
                        <Input placeholder="例如: 公司内网或中国电信" />
                    </Form.Item>

                    <Form.Item
                        name="ip_version"
                        label="IP版本"
                    >
                        <Radio.Group>
                            <Radio value="both">自动检测</Radio>
                            <Radio value="ipv4">IPv4</Radio>
                            <Radio value="ipv6">IPv6</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 批量导入模态框 */}
            <Modal
                title="批量导入CIDR条目"
                open={isBulkModalVisible}
                onOk={handleBulkImportOk}
                onCancel={() => setIsBulkModalVisible(false)}
                confirmLoading={loading}
                width={600}
            >
                <Form
                    form={bulkForm}
                    layout="vertical"
                >
                    <Alert
                        message="批量导入格式说明"
                        description={
                            <div>
                                <p>每行一个条目，格式为：</p>
                                <Text code>CIDR|描述|IP版本</Text>
                                <p>其中：</p>
                                <ul>
                                    <li>CIDR为必填项，例如：192.168.1.0/24 或 2001:db8::/64</li>
                                    <li>描述为可选项</li>
                                    <li>IP版本为可选项，可选值为：both (自动检测), ipv4, ipv6</li>
                                </ul>
                                <p>示例：</p>
                                <Text code>192.168.1.0/24|公司内网|ipv4</Text>
                                <Text code>2001:db8::/64|IPv6测试网络</Text>
                            </div>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />

                    <Form.Item
                        name="entries"
                        label="CIDR条目"
                        rules={[{ required: true, message: '请输入要导入的CIDR条目!' }]}
                    >
                        <TextArea rows={10} placeholder="每行一个CIDR条目" />
                    </Form.Item>

                    <Form.Item>
                        <Upload.Dragger
                            name="file"
                            beforeUpload={handleFileUpload}
                            showUploadList={false}
                            accept=".txt,.csv"
                        >
                            <p className="ant-upload-drag-icon">
                                <CloudUploadOutlined />
                            </p>
                            <p className="ant-upload-text">点击或拖拽文件到此处上传</p>
                            <p className="ant-upload-hint">支持.txt或.csv文件</p>
                        </Upload.Dragger>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default DnsCidrEntries;
